import scopeLinkedPostsView from './scopeLinkedPostsComponent.html';
import './scopeLinkedPostsComponent.css';
import { cloneDeep } from 'lodash';

export function scopeLinkedPostsComponent() {
	return {
		bindings: {
			post: '<',
		},
		controllerAs: 'vm',
		controller: scopeLinkedPostsComponentCtrl,
		template: scopeLinkedPostsView,
	};
}

export class scopeLinkedPostsComponentCtrl {
	constructor($scope, Cfg, Api, ScrollManager) {
		'ngInject';
		this.$scope = $scope;
		this.Cfg = Cfg;
		this.Api = Api;
		this.ScrollManager = ScrollManager;
		this.scrollerHandler = null;
		this.scrollElement = document.getElementsByClassName('sh-modal-content')[0] || document;
		this.enabledScopeTypes = [];
		// this.availableScopeTypes = [];
		this.selectedScope = '';
		this.v3SearchCriteria = null;
		this.searchStatus = '';
		this.scopeLinkedPosts = [];
		this.nextPageUrl = '';
		this.isLastPage = false;
		this.totalRecords = 0;
	}

	/**
	 * IMPORTANT: This method must be "async" because "await" is required for _identifyAvailableScopeQueries.
	 * @returns {Promise<void>}
	 */
	async $onInit() {
		this.availableScopeTypes = await this._identifyAvailableScopeQueries(); // IMPORTANT: Must be awaited or a race condition will occur.
		this._getInitialScopeLinkedPosts();
		this._registerScrollHandler();
	}

	$onDestroy() {
		this._unregisterScrollHandler();
	}

	/**
	 * Sets up events to append more scope-linked posts
	 * @private
	 */
	_registerScrollHandler() {
		// To avoid duplication, only register the scroller handler if it currently is missing.
		if (!this.scrollerHandler) {
			this.scrollerHandler = scroll => {
				// If the user has scrolled to within 1% of the bottom edge of the document scrollbar, send a request
				// for another "page" of scope-related posts to append to the current list.
				if (
					this.searchStatus === 'loaded' && // Using the flags to avoid duplicate simultaneous requests.
					this.nextPageUrl && // Making sure that there is an non-empty "next page" URL value available to submit.
					!this.isLastPage && // Making sure that there actually are more pages waiting.
					!isNaN(parseFloat(scroll.ratioBottom)) && // Making sure the vertical scrollbar data is present.
					scroll.ratioBottom >= 0.95 // Triggering. NOTE: 99% means that we're within 5% of the bottom edge.
				) {
					// Flag that we're starting the loading process.
					this.searchStatus = 'loading';

					return this.Api.request('GET', this.nextPageUrl).then(resp => {
						// Get parameters for the next search from the endpoint.
						this.nextPageUrl = resp.headers('API-Next-Page');
						this.isLastPage = resp.headers('API-Is-Last-Page') === '1';
						this.totalRecords = resp.headers('API-Total-Records');
						this.scopeLinkedPosts = this.scopeLinkedPosts.concat(this._formatScopeLinkedPosts(resp.data ?? []));

						// Flag that the search has finished loading.
						this.searchStatus = 'loaded';
					});
				}
			};

			this.ScrollManager.register(this.scrollElement, this.scrollerHandler);
		}
	}

	/**
	 * Unregisters the scroller handler (previously registered by _registerScrollHandler).
	 * @private
	 */
	_unregisterScrollHandler() {
		if (this.scrollerHandler) {
			this.ScrollManager.unregister(this.scrollElement, this.scrollerHandler);
		}
	}

	/**
	 * Sets scope types based on client preferences.
	 * IMPORTANT: This method must be "async" because the "taxonomy" option must be awaited to avoid a race condition.
	 * @returns {Promise<void>}
	 * @private
	 */
	async _identifyAvailableScopeQueries() {
		let availableScopeTypes = [];

		// Acquire the enabled scopes settings from the preferences.
		const enabledScopes = this.Cfg.read('LinkedPost.enabled_scopes');

		if (enabledScopes) {
			// Transform and normalize the values in the enabled scopes.
			this.enabledScopeTypes = enabledScopes
				.split(',')
				.map(type => type.trim())
				.filter(type => type !== '');
		}

		// For "taxonomy" to be included in the list, it must be specified in the preferences -AND- at least one active
		// taxonomy option must exist for this post.
		if (this.enabledScopeTypes.includes('taxonomy')) {
			// Look up the taxonomy option value(s) for this post.
			await this.Api.request('GET', `/v2/posts/${this.post.id}/taxonomy_options`, {
				view_related_posts: true,
			}).then(resp => {
				const options = resp.data || {}; // NOTE: If the data is empty, then there are no options.

				let index = 0;

				for (const name in options) {
					availableScopeTypes.push({
						id: `taxonomy-${index}`,
						scope: 'taxonomy',
						name: `${name}: ${options[name]}`,
						criteria: { taxonomies: `${name}:${options[name]}` },
					});

					index++;
				}
			});
		}

		// For "category" to be included in the list, it must be specified in the preferences -AND- a category record must
		// be associated with this post.
		if (this.enabledScopeTypes.includes('category') && this.post.category && this.post.category.id) {
			availableScopeTypes.push({
				id: 'category',
				scope: 'category',
				name: `Category: ${this.post.category.name}`,
				criteria: { category_id: this.post.category.id },
			});
		}

		// For "classification" to be included in the list, it must be specified in the preferences -AND- a classification
		// record must be associated with this post.
		if (this.enabledScopeTypes.includes('classification') && this.post.classification && this.post.classification.id) {
			availableScopeTypes.push({
				id: 'classification',
				scope: 'classification',
				name: `Classification: ${this.post.classification.name}`,
				criteria: { classification_id: this.post.classification.id },
			});
		}

		// For "publisher" to be included in the list, it must be specified in the preferences -AND- the published_by
		// user record be associated with this post.
		if (this.enabledScopeTypes.includes('publisher') && this.post.published_by && this.post.published_by.id) {
			availableScopeTypes.push({
				id: 'publisher',
				scope: 'publisher',
				name: `Publisher: ${this.post.published_by.fname} ${this.post.published_by.lname}`,
				criteria: { publisher: this.post.published_by.id },
			});
		}

		// For "website" to be included in the list, it must be specified in the preferences -AND- a non-empty value for
		// must be set in the post record.
		if (this.enabledScopeTypes.includes('website') && this.post.domain_name) {
			availableScopeTypes.push({
				id: 'website',
				scope: 'website',
				name: `Website: ${this.post.domain_name}`,
				criteria: { domain_names: [this.post.domain_name] },
			});
		}

		// Re-order the enabledScopeTypes to honor the default choice in the preferences.
		const enabledScopesDefault = this.Cfg.read('LinkedPost.enabled_scopes_default');

		// Of course, we only need to change the ordering if the selected default is something other than "taxonomy" and
		// it actually appears in the list of enabled scopes.
		if (enabledScopesDefault !== 'taxonomy' && this.enabledScopeTypes.includes(enabledScopesDefault)) {
			// If the preferences indicate a default other than "taxonomy", move it to the front of the list.
			this.enabledScopeTypes = this.enabledScopeTypes.filter(s => s !== enabledScopesDefault);
			this.enabledScopeTypes.unshift(enabledScopesDefault);

			// NOTE: This only works because "taxonomy" is excluded in the test above.
			const defaultAvailableScopeType = availableScopeTypes.find(t => t.scope === enabledScopesDefault);

			if (defaultAvailableScopeType) {
				availableScopeTypes = availableScopeTypes.filter(t => t.scope !== enabledScopesDefault);

				availableScopeTypes.unshift(defaultAvailableScopeType);
			}
		}

		return availableScopeTypes;
	}

	/**
	 * Fetches the initial page of post per the criteria.
	 * @returns {*}
	 * @private
	 */
	_getInitialScopeLinkedPosts() {
		if (this.availableScopeTypes.length > 0) {
			// For the initial query, we will choose the first registered scope type and its criteria.
			this.selectedScope = this.availableScopeTypes[0].id;
			this.v3SearchCriteria = cloneDeep(this.availableScopeTypes[0].criteria);

			// Set additional criteria to qualify and limit the post result set.
			this.v3SearchCriteria['not_ids'] = [this.post.id]; // NOTE: Keep the current post out of the search results.
			this.v3SearchCriteria['order_by'] = 'publish_date'; // NOTE: This *should* return in DESC date order.
			this.v3SearchCriteria['limit'] = 3; // NOTE: For the desired display, each page is a maximum of three posts.

			// Flag that we're starting the loading process.
			this.searchStatus = 'loading';

			return this.Api.request('GET', '/v3/posts/search', this.v3SearchCriteria).then(resp => {
				// Get parameters for the next search from the endpoint.
				this.nextPageUrl = resp.headers('API-Next-Page');
				this.isLastPage = resp.headers('API-Is-Last-Page') === '1';
				this.totalRecords = resp.headers('API-Total-Records');
				this.scopeLinkedPosts = this._formatScopeLinkedPosts(resp.data ?? []);

				// Flag that the search has finished loading. IMPORTANT: Required state for loading next pages.
				this.searchStatus = 'loaded';
			});
		}
	}

	/**
	 * Fetches the initial page of the replacement posts based on the user's selection.
	 * NOTE: Notice the high degree of similarity between this method and _getInitialScopeLinkedPosts.
	 * @returns {*}
	 */
	onRelatedScopeChange() {
		// Look up the new scoped query based on the incoming scope identifier and submit the new base query.
		this.v3SearchCriteria = cloneDeep(this.availableScopeTypes.find(scope => scope.id === this.selectedScope).criteria);

		// Set the extra criteria...
		this.v3SearchCriteria['not_ids'] = [this.post.id]; // NOTE: This keeps the current post out of the search results.
		this.v3SearchCriteria['order_by'] = 'publish_date'; // NOTE: This *should* return in DESC date order.
		this.v3SearchCriteria['limit'] = 3; // NOTE: For the desired display, each page is a maximum of three posts.

		// Flag that we're starting the loading process.
		this.searchStatus = 'loading';

		// NOTE: This will be a replacement query for the existing display -AND NOT- a new set to be appended.
		return this.Api.request('GET', '/v3/posts/search', this.v3SearchCriteria).then(resp => {
			// Get parameters for the next search from the endpoint.
			this.nextPageUrl = resp.headers('API-Next-Page');
			this.isLastPage = resp.headers('API-Is-Last-Page') === '1';
			this.totalRecords = resp.headers('API-Total-Records');
			this.scopeLinkedPosts = this._formatScopeLinkedPosts(resp.data ?? []);

			// Flag that the search has finished loading. IMPORTANT: Required state for loading next pages.
			this.searchStatus = 'loaded';
		});
	}

	/**
	 * Perform a small transformation of post's headline, if necessary.
	 * NOTE: This is a private method.
	 * @param posts
	 * @returns {*}
	 * @private
	 */
	_formatScopeLinkedPosts(posts) {
		// If there are any posts, update their headline value with the formatting transformation.
		if (posts instanceof Array) {
			for (let post of posts) {
				// If the post's headline is a string with no whitespace characters, force whitespaces into it.
				if (post.headline.length > 39 && /^[^\s]{39,}/.test(post.headline)) {
					post.headline = post.headline.match(/.{1,39}/g).join(' ');
				}
			}
		}

		return posts;
	}

	/**
	 * This function inspects the properties of the post object and choose which image will be displayed based on what
	 * is set.
	 * @param post
	 * @returns {string|*}
	 */
	getImageSrc(post) {
		// The first choice for image will always be the specifically set image, if it exists.
		if (post.image && post.image.url) {
			return post.image.url;
		}

		// The second choice for image will be the first attachment's first page image, if it is set.
		if (post.attachment_one && post.attachment_one.path) {
			return post.attachment_one.path;
		}

		// If neither the image nor the page image is set, determine if the type of the post is one of the standard
		// file types and, if it is, display a type-relevant placeholder icon instead of an image.
		if (
			post.attachment_icon_classes &&
			post.attachment_icon_classes[0] &&
			['icon-file-pdf', 'icon-file-word', 'icon-file-powerpoint'].includes(post.attachment_icon_classes[0])
		) {
			if (post.attachment_icon_classes[0] === 'icon-file-pdf') {
				return '/assets/img/filetypes/secondary/pdf.png';
			}

			if (post.attachment_icon_classes[0] === 'icon-file-word') {
				return '/assets/img/filetypes/secondary/doc.png';
			}

			return '/assets/img/filetypes/secondary/ppt.png';
		}

		// And if no other match can be found above, return the default icon instead of any other image.
		return '/assets/theme/sharpr/missing.png';
	}
}
